import { useEffect, useState } from 'react';
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { collapseDuplicateCareSources } from './careSourceHistoryDeduplication';
import { careSourceMatchesScope } from './careSourceScope';
import { sortCareSourcesByNewest } from './careSourceStatusModel';
import { normalizeCareDocumentSource } from './careDocumentSourceService';

const SOURCE_NOTES_COLLECTION = 'careSources';

export const useCareSourceHistory = ({
  childId = '',
  sourceContext = '',
  documentType = '',
  sourceKind = '',
  sourceType = '',
  includeDuplicates = false,
} = {}) => {
  const [sources, setSources] = useState([]);
  const [hiddenDuplicateCount, setHiddenDuplicateCount] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(Boolean(childId));

  useEffect(() => {
    if (!childId) {
      setSources([]);
      setHiddenDuplicateCount(0);
      setError(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const sourceQuery = query(
      collection(db, SOURCE_NOTES_COLLECTION),
      where('childId', '==', childId)
    );

    return onSnapshot(
      sourceQuery,
      (snapshot) => {
        const nextSources = snapshot.docs
          .map((doc) => normalizeCareDocumentSource({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((source) => careSourceMatchesScope(source, {
            sourceContext,
            documentType,
            sourceKind,
            sourceType,
          }))
          .filter((source) => source.status !== 'deleted');

        const sorted = sortCareSourcesByNewest(nextSources);
        const collapsedHistory = includeDuplicates ? {sources:sorted,hiddenDuplicateCount:0} : collapseDuplicateCareSources(sorted);
        setSources(collapsedHistory.sources);
        setHiddenDuplicateCount(collapsedHistory.hiddenDuplicateCount);
        setError(null);
        setLoading(false);
      },
      (snapshotError) => {
        setError(snapshotError);
        setLoading(false);
      }
    );
  }, [childId, documentType, sourceContext, sourceKind, sourceType, includeDuplicates]);

  return {
    error,
    hiddenDuplicateCount,
    loading,
    sources,
  };
};

export default useCareSourceHistory;
